import { Routes, Route, Navigate } from "react-router-dom";
import { useEffect, lazy, Suspense, useState } from "react";
import { ScrollToTop } from "./components/ScrollToTop";
import { GlobalPageEnhancements } from "./components/GlobalPageEnhancements";
import { LegacyUrlRedirect } from "./components/LegacyUrlRedirect";
import { OutboundLinkTracker } from "./components/OutboundLinkTracker";
import { getKeptGewerbegebietSlugs, getRedirectedGewerbegebietSlugs } from "./data/gewerbegebietePages";
import Hamburg from "./pages/Hamburg";

const BackToTopButton = lazy(() =>
  import("./components/BackToTopButton").then((module) => ({ default: module.BackToTopButton })),
);
const SiteAssistant = lazy(() =>
  import("./components/SiteAssistant").then((module) => ({ default: module.SiteAssistant })),
);
const CookieBanner = lazy(() => import("./components/CookieBanner"));

const Index = lazy(() => import("./pages/Index"));
const LagerhalleMieten = lazy(() => import("./pages/LagerhalleMieten"));
const Berlin = lazy(() => import("./pages/Berlin"));
const Muenchen = lazy(() => import("./pages/Muenchen"));
const Frankfurt = lazy(() => import("./pages/Frankfurt"));
const Koeln = lazy(() => import("./pages/Koeln"));
const Duesseldorf = lazy(() => import("./pages/Duesseldorf"));
const Leipzig = lazy(() => import("./pages/Leipzig"));
const Stuttgart = lazy(() => import("./pages/Stuttgart"));
const Finder = lazy(() => import("./pages/Finder"));
const GewerbegebietFinden = lazy(() => import("./pages/GewerbegebietFinden"));
const Impressum = lazy(() => import("./pages/Impressum"));
const Datenschutz = lazy(() => import("./pages/Datenschutz"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Auth = lazy(() => import("./pages/Auth"));
const Admin = lazy(() => import("./pages/Admin"));
const Ressourcen = lazy(() => import("./pages/Ressourcen"));
const Reviews = lazy(() => import("./pages/Reviews"));
const ImmozentraleReview = lazy(() => import("./pages/reviews/ImmozentraleReview"));
const FrankHoffmannReview = lazy(() => import("./pages/reviews/FrankHoffmannReview"));
const GewerbeimmobilienVerkaufen = lazy(() => import("./pages/GewerbeimmobilienVerkaufen"));
const ImmoweltOderImmobilienscout24 = lazy(() => import("./pages/ImmoweltOderImmobilienscout24"));
const ImmobilienRaiffeisenbank = lazy(() => import("./pages/ImmobilienRaiffeisenbank"));
const BueroKoelnMieten = lazy(() => import("./pages/BueroKoelnMieten"));
const UeberUns = lazy(() => import("./pages/UeberUns"));
const GewerbeversicherungenRatgeber = lazy(() => import("./pages/GewerbeversicherungenRatgeber"));
const ContentManager = lazy(() => import("./pages/ContentManager"));
const GewerbegebieteUebersicht = lazy(() => import("./pages/GewerbegebieteUebersicht"));
const BerechnungVonMiete = lazy(() => import("./pages/BerechnungVonMiete"));
const GewerbeimmobilienTrends = lazy(() => import("./pages/GewerbeimmobilienTrends"));
const GewerbegebietPage = lazy(() => import("./pages/GewerbegebietPage"));
const EnergieausweisGewerbeimmobilien = lazy(() => import("./pages/EnergieausweisGewerbeimmobilien"));
const RaumMietenInDerNaehe = lazy(() => import("./pages/RaumMietenInDerNaehe"));
const SaalMietenInDerNaehe = lazy(() => import("./pages/SaalMietenInDerNaehe"));
const BueroInMeinerNaehe = lazy(() => import("./pages/BueroInMeinerNaehe"));
const LadenflaeecheMieten = lazy(() => import("./pages/LadenflaeecheMieten"));
const GeschaeftsraeumeMieten = lazy(() => import("./pages/GeschaeftsraeumeMieten"));
const IndustriegebietInMeinerNaehe = lazy(() => import("./pages/IndustriegebietInMeinerNaehe"));
const Barrierefreiheit = lazy(() => import("./pages/Barrierefreiheit"));
const DynamicReview = lazy(() => import("./pages/DynamicReview"));
const MieteinnahmenVersteuernRechner = lazy(() => import("./pages/MieteinnahmenVersteuernRechner"));
const DynamicArticle = lazy(() => import("./pages/DynamicArticle"));
const IndexmieteBerechnen = lazy(() => import("./pages/IndexmieteBerechnen"));
const IndexmieteRatgeber = lazy(() => import("./pages/IndexmieteRatgeber"));
const RechnerLagerhalleMietpreis = lazy(() => import("./pages/rechner/LagerhalleMietpreis"));
const RechnerGewerbemieteProQm = lazy(() => import("./pages/rechner/GewerbemieteProQm"));
const RechnerVergleichsmiete = lazy(() => import("./pages/rechner/OrtsuebicheVergleichsmiete"));
const RechnerKaltmieteGewerbe = lazy(() => import("./pages/rechner/KaltmieteGewerbe"));
const RechnerVermieterMietpreis = lazy(() => import("./pages/rechner/VermieterMietpreis"));

const DomainRedirect = () => {
  useEffect(() => {
    const hostname = window.location.hostname;
    const isPreview = hostname.endsWith(".lovable.app");
    const isLocal = hostname === "localhost" || hostname.startsWith("127.") || hostname.startsWith("192.168.");

    if (isPreview) {
      const metaRobots = document.createElement("meta");
      metaRobots.name = "robots";
      metaRobots.content = "noindex, nofollow";
      document.head.appendChild(metaRobots);
      return () => metaRobots.remove();
    }

    if (hostname !== "gewerbeflaeche-mieten.de" && !isLocal) {
      window.location.replace(`https://gewerbeflaeche-mieten.de${window.location.pathname}${window.location.search}${window.location.hash}`);
    }
  }, []);
  return null;
};

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-pulse text-muted-foreground">Laden…</div>
  </div>
);

export const AppRoutes = () => {
  const [loadDeferredUi, setLoadDeferredUi] = useState(false);

  useEffect(() => {
    if (loadDeferredUi) return;

    const enableDeferredUi = () => setLoadDeferredUi(true);
    const timeoutId = window.setTimeout(enableDeferredUi, 2000);

    window.addEventListener("pointerdown", enableDeferredUi, { once: true, passive: true });
    window.addEventListener("keydown", enableDeferredUi, { once: true });
    window.addEventListener("scroll", enableDeferredUi, { once: true, passive: true });

    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener("pointerdown", enableDeferredUi);
      window.removeEventListener("keydown", enableDeferredUi);
      window.removeEventListener("scroll", enableDeferredUi);
    };
  }, [loadDeferredUi]);

  return (
    <>
      <ScrollToTop />
      <DomainRedirect />
      <LegacyUrlRedirect />
      <OutboundLinkTracker />
      <GlobalPageEnhancements />

      <Suspense fallback={null}>
        {loadDeferredUi && (
          <>
            <CookieBanner />
            <SiteAssistant />
            <BackToTopButton />
          </>
        )}
      </Suspense>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/lagerhalle-mieten" element={<LagerhalleMieten />} />
          <Route path="/gewerbeflaeche-mieten-berlin" element={<Berlin />} />
          <Route path="/gewerbeflaeche-mieten-hamburg" element={<Hamburg />} />
          <Route path="/gewerbeflaeche-mieten-muenchen" element={<Muenchen />} />
          <Route path="/gewerbeflaeche-mieten-frankfurt" element={<Frankfurt />} />
          <Route path="/gewerbeflaeche-mieten-koeln" element={<Koeln />} />
          <Route path="/gewerbeflaeche-mieten-duesseldorf" element={<Duesseldorf />} />
          <Route path="/leipzig-gewerbeimmobilien" element={<Leipzig />} />
          <Route path="/stuttgart-gewerbeimmobilien" element={<Stuttgart />} />
          <Route path="/berlin" element={<Navigate to="/gewerbeflaeche-mieten-berlin" replace />} />
          <Route path="/hamburg" element={<Navigate to="/gewerbeflaeche-mieten-hamburg" replace />} />
          <Route path="/muenchen" element={<Navigate to="/gewerbeflaeche-mieten-muenchen" replace />} />
          <Route path="/frankfurt" element={<Navigate to="/gewerbeflaeche-mieten-frankfurt" replace />} />
          <Route path="/koeln" element={<Navigate to="/gewerbeflaeche-mieten-koeln" replace />} />
          <Route path="/duesseldorf" element={<Navigate to="/gewerbeflaeche-mieten-duesseldorf" replace />} />
          <Route path="/portal-finder" element={<Finder />} />
          <Route path="/gewerbegebiet-finden" element={<GewerbegebietFinden />} />
          <Route path="/gewerbegebiete-deutschland" element={<GewerbegebieteUebersicht />} />
          <Route path="/ressourcen" element={<Ressourcen />} />
          <Route path="/rezension" element={<Reviews />} />
          <Route path="/rezension/immozentrale-eu" element={<ImmozentraleReview />} />
          <Route path="/rezension/frank-hoffmann-immobilien" element={<FrankHoffmannReview />} />
          <Route path="/rezension/realogis-de" element={<Navigate to="/rezension/realogis-review" replace />} />
          <Route path="/rezension/:slug" element={<DynamicReview />} />
          <Route path="/ressourcen/gewerbeimmobilien-verkaufen" element={<GewerbeimmobilienVerkaufen />} />
          <Route path="/ressourcen/immowelt-oder-immobilienscout24" element={<ImmoweltOderImmobilienscout24 />} />
          <Route path="/ressourcen/immobilien-raiffeisenbank" element={<ImmobilienRaiffeisenbank />} />
          <Route path="/ressourcen/buero-koeln-mieten" element={<BueroKoelnMieten />} />
          <Route path="/ressourcen/gewerbeimmobilien-trends" element={<GewerbeimmobilienTrends />} />
          <Route path="/ressourcen/energieausweis-gewerbeimmobilien" element={<EnergieausweisGewerbeimmobilien />} />
          <Route path="/berechnung-von-miete" element={<BerechnungVonMiete />} />
          <Route path="/berechnung-of-miete" element={<Navigate to="/berechnung-von-miete" replace />} />
          <Route path="/gewerbeversicherungen-vergleich" element={<GewerbeversicherungenRatgeber />} />
          <Route path="/raum-mieten-in-der-naehe" element={<RaumMietenInDerNaehe />} />
          <Route path="/saal-mieten-in-der-naehe" element={<SaalMietenInDerNaehe />} />
          <Route path="/lager-mieten-in-der-naehe" element={<Navigate to="/lagerhalle-mieten" replace />} />
          <Route path="/buero-in-meiner-naehe" element={<BueroInMeinerNaehe />} />
          <Route path="/lager-zu-mieten" element={<Navigate to="/lagerhalle-mieten" replace />} />

          <Route path="/ladenflaeche-mieten" element={<LadenflaeecheMieten />} />
          <Route path="/ladenfleache-mieten" element={<Navigate to="/ladenflaeche-mieten" replace />} />
          <Route path="/geschaeftsraeume-mieten" element={<GeschaeftsraeumeMieten />} />
          <Route path="/industriegebiet-in-meiner-naehe" element={<IndustriegebietInMeinerNaehe />} />
          <Route path="/ueber-uns" element={<UeberUns />} />
          <Route path="/impressum" element={<Impressum />} />
          <Route path="/datenschutz" element={<Datenschutz />} />
          <Route path="/barrierefreiheit" element={<Barrierefreiheit />} />
          <Route path="/indexmiete-berechnen" element={<IndexmieteBerechnen />} />
          <Route path="/indexmiete-rechner" element={<Navigate to="/indexmiete-berechnen" replace />} />
          <Route path="/mieteinnahmen-versteuern-rechner" element={<MieteinnahmenVersteuernRechner />} />
          <Route path="/rechner/lagerhalle-mietpreis" element={<RechnerLagerhalleMietpreis />} />
          <Route path="/rechner/gewerbemiete-pro-qm" element={<RechnerGewerbemieteProQm />} />
          <Route path="/rechner/ortsuebliche-vergleichsmiete-gewerbe" element={<RechnerVergleichsmiete />} />
          <Route path="/rechner/kaltmiete-gewerbe" element={<RechnerKaltmieteGewerbe />} />
          <Route path="/rechner/vermieter-mietpreis" element={<RechnerVermieterMietpreis />} />
          <Route path="/rechner/mieteinnahmen-versteuern" element={<Navigate to="/mieteinnahmen-versteuern-rechner" replace />} />
          <Route path="/versteuerung-mieteinnahmen-rechner" element={<Navigate to="/mieteinnahmen-versteuern-rechner" replace />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/content-manager" element={<ContentManager />} />
          {getKeptGewerbegebietSlugs().map((slug) => (
            <Route key={slug} path={`/${slug}`} element={<GewerbegebietPage slug={slug} />} />
          ))}
          {getRedirectedGewerbegebietSlugs().map((slug) => (
            <Route
              key={`redirect-${slug}`}
              path={`/${slug}`}
              element={<Navigate to="/gewerbegebiete-deutschland" replace />}
            />
          ))}

          <Route path="/ressourcen/indexmiete" element={<IndexmieteRatgeber />} />
          <Route path="/ressourcen/gewerbeflaeche-berlin" element={<Navigate to="/gewerbeflaeche-mieten-berlin" replace />} />
          <Route path="/ressourcen/*" element={<DynamicArticle />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
};

export default AppRoutes;
